import { type FunctionComponent, useEffect, useMemo, useState } from "react";
import {
  HomepageV2ThemeFindOutMoreDisplay,
  HomepageV2ThemeSignUpButtonDisplay,
  NewsletterVariant,
  useNewsletterApi,
  useSignedUpNewsletter,
} from "scmp-sdk";
import type { NewsletterItem } from "scmp-sdk/components/newsletter/types";

import type { NewsletterPackageDetail, ValidNewsletterPackageKey } from "shared/data/newsletter";
import { Edition as EditionEnum, useEditionValue } from "shared/lib/edition";
import { notEmpty } from "shared/lib/utils";

import { getNextNewsletterPackage } from "scmp-app/components/newsletter/helpers";
import { NewsletterWidgetContainer } from "scmp-app/components/newsletter/newsletter-widget-container";
import { useNewsletterWidget } from "scmp-app/components/newsletter/newsletter-widget-container/hooks";

export type Props = {
  className?: string;
};

export const HomeV2Newsletter: FunctionComponent<Props> = ({ className }) => {
  const edition = useEditionValue();
  const subscribedPackageIds = useSignedUpNewsletter();
  const { fetchNewsletterByIds, isNewslettersDataReady } = useNewsletterApi();

  const { dataMatrix } = useNewsletterWidget({
    contentType: "homepage",
    eventCategory: "Newsletter (Homepage - Inline)",
    section: "",
  });

  const [packageKey, setPackageKey] = useState<ValidNewsletterPackageKey>();
  const [selectedPackage, setSelectedPackage] = useState<NewsletterPackageDetail>();
  const [childNewsletterItems, setChildNewsletterItems] = useState<NewsletterItem[]>();

  const placement = useMemo(() => {
    switch (edition) {
      case EditionEnum.Asia:
        return "HP/ASIA";
      case EditionEnum.HongKong:
        return "HP/HK";
      default:
        return "HP/INT";
    }
  }, [edition]);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const packageToShow = await getNextNewsletterPackage(placement, subscribedPackageIds);
        if (cancelled) return;
        const packageDetail = packageToShow?.packageDetail;
        setSelectedPackage(packageDetail);
        setPackageKey(packageToShow?.packageKey);

        if (notEmpty(packageDetail?.childNewsletterIds)) {
          const data = await fetchNewsletterByIds(
            packageDetail.childNewsletterIds,
            packageDetail.childNewsletterIds.length,
          );
          if (cancelled) return;
          setChildNewsletterItems(data?.filter(notEmpty) ?? []);
          return;
        }
        setChildNewsletterItems([]);
      } catch {
        if (!cancelled) setChildNewsletterItems([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [fetchNewsletterByIds, placement, subscribedPackageIds]);

  if (!isNewslettersDataReady && childNewsletterItems === undefined) return null;

  return (
    <NewsletterWidgetContainer
      className={className}
      dataMatrix={dataMatrix}
      findOutMoreButtonDisplayComponent={<HomepageV2ThemeFindOutMoreDisplay />}
      multipleNewsletters
      newsletterItems={childNewsletterItems ?? []}
      packageDisplayName={selectedPackage?.packageDisplayName}
      packageKey={packageKey}
      signUpButtonDisplayComponent={<HomepageV2ThemeSignUpButtonDisplay />}
      useOnlyPackageNewsletters
      variant={NewsletterVariant.HomepageV2}
    />
  );
};

HomeV2Newsletter.displayName = "HomeV2Newsletter";
